import React, { PureComponent } from 'react';
import compose from 'lodash/flowRight';
import { string, arrayOf, object, func, number } from 'prop-types';
import { connectStats } from 'react-instantsearch-core';
import classnames from 'classnames';
import { promotedFiltersShape } from 'shapes';
import { SwitchFadeTransition } from 'components';
import { withUrlParams } from 'connectors';
import ResultLength from '../ResultLength/ResultLength';
import {
    PromotedFiltersStep,
    NavigationTabs,
    PromotedFiltersSummary,
} from './components';
import {
    getCurrentPromotedFiltersStep,
    getTabNames,
    getAppliedPastilles,
    getStepIndexByPastille,
} from './utils/guidedNavigation';

const MIN_WAIT_TRANSITION = 500;

class PromotedFilters extends PureComponent {
    static propTypes = {
        className: string,
        promotedFilters: arrayOf(promotedFiltersShape).isRequired,
        currentRefinements: arrayOf(object).isRequired,
        refine: func.isRequired,
        nbHits: number.isRequired,
    };

    static defaultProps = {
        className: null,
    };

    static getDerivedStateFromProps(props, state) {
        let activeTab = state.activeTab;
        let latency = 0;
        const now = Date.now();
        if (props.currentRefinements !== state.currentRefinements) {
            latency = MIN_WAIT_TRANSITION - (now - state.changeStepTimestamp);
        }

        const pfStep = props.searchState?.manomano?.urlParams?.pfStep;
        activeTab = typeof pfStep !== 'undefined' ? pfStep : -1;

        const stepKey = latency > 0 ? 'nextStep' : 'currentStep';
        return {
            [stepKey]: getCurrentPromotedFiltersStep(
                props.promotedFilters,
                props.currentRefinements,
                activeTab,
            ),
            latency,
            currentRefinements: props.currentRefinements,
        };
    }

    state = {};

    componentDidUpdate() {
        const { latency } = this.state;
        if (latency > 0) {
            setTimeout(() => {
                this.setState(({ nextStep }) => ({
                    latency: 0,
                    currentStep: nextStep,
                    nextStep: null,
                }));
            }, latency);
        }
    }

    getTransitionKey = step => (step ? `${step.id}` : 'summary');

    getStepAttributes = step =>
        step ? step.pastilles.map(pastille => pastille.attributes).flat() : [];

    getActiveTabIndex = (promotedFilters, currentStep) =>
        currentStep
            ? promotedFilters.findIndex(item => item.id === currentStep.id)
            : -1;

    handleNavigationTabChange = (evt, activeTab) => {
        this.setState({
            activeTab,
        });
        this.props.refine('pfStep', activeTab);
    };

    handleFinalSummarySelectPastille = (evt, pastille) => {
        this.setState(
            {
                activeTab: getStepIndexByPastille(
                    this.props.promotedFilters,
                    pastille,
                ),
            },
            () => this.props.refine('pfStep', this.state.activeTab),
        );
    };

    handleSelectPastille = (clickedOnSamePastille = false) => {
        this.setState(
            {
                changeStepTimestamp: Date.now(),
                activeTab: -1,
            },
            () => {
                if (clickedOnSamePastille) {
                    this.props.refine('pfStep', this.state.activeTab);
                }
            },
        );
    };

    render() {
        const {
            className,
            promotedFilters,
            currentRefinements,
            nbHits,
        } = this.props;
        const { currentStep } = this.state;
        const rootClassnames = classnames(
            'promoted-filters',
            'promoted-filters--animated',
            className,
        );

        const attributes = this.getStepAttributes(currentStep);
        const tabNames = getTabNames(promotedFilters, currentRefinements);
        const activeTabIndex = this.getActiveTabIndex(
            promotedFilters,
            currentStep,
        );

        return (
            <div className={rootClassnames}>
                <NavigationTabs
                    className="promoted-filters__navigation-tabs"
                    tabs={tabNames}
                    activeTab={activeTabIndex}
                    onTabChange={this.handleNavigationTabChange}
                />

                <div className="promoted-filters__step-and-summary-wrapper">
                    <SwitchFadeTransition
                        keyIn={this.getTransitionKey(currentStep)}
                    >
                        {currentStep ? (
                            <PromotedFiltersStep
                                className="promoted-filters__step"
                                promotedFilters={currentStep}
                                attributes={attributes}
                                onSelectPastille={this.handleSelectPastille}
                            />
                        ) : (
                            <PromotedFiltersSummary
                                className="promoted-filters__summary"
                                pastilles={getAppliedPastilles(
                                    promotedFilters,
                                    currentRefinements,
                                )}
                                onSelectPastille={
                                    this.handleFinalSummarySelectPastille
                                }
                            />
                        )}
                    </SwitchFadeTransition>
                </div>

                <div className="promoted-filters__result-number">
                    <ResultLength nbHits={nbHits} />
                </div>
            </div>
        );
    }
}

export default compose(withUrlParams, connectStats)(PromotedFilters);
